/**
 * Le Rideau Isolant — le sélecteur « 1 ou 2 rideaux » de la fiche produit (bloc `luxe-duo`).
 *
 * Rien n'est calculé ici qui compte pour le paiement : la remise est une
 * réduction automatique Shopify. Le composant fait deux choses : régler la
 * quantité du formulaire d'ajout au panier, et se remettre au tarif du format
 * choisi quand on change de dimension.
 *
 * Le thème ne re-rend pas la fiche entière au changement de variante : chaque
 * composant va chercher son propre fragment dans le HTML que le thème vient de
 * demander au serveur (`event.promise`), comme le fait `product-price`. On
 * reprend donc les prix rendus par Liquid au lieu de les recalculer ici, sinon
 * l'encart reste bloqué sur le prix du format affiché au chargement.
 *
 * L'état visuel des deux cartes tient au `:checked` des radios, en CSS seule :
 * le JavaScript ne fait que pousser et relire la quantité.
 */

import { StandardEvents } from '@shopify/events';

class LuxeDuo extends HTMLElement {
  #ecoute = false;
  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    if (!this.#ecoute) {
      this.#ecoute = true;
      this.addEventListener('change', this.#surChoix);
    }
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#surVariante);
    document.addEventListener('quantity-selector:update', this.#surQuantite);
    this.#refleter();
  }

  disconnectedCallback() {
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#surVariante);
    document.removeEventListener('quantity-selector:update', this.#surQuantite);
  }

  /** La fiche produit courante : le sélecteur de quantité du panier ne nous regarde pas. */
  get #portee() {
    return this.#section ?? this.closest('.shopify-section') ?? document;
  }

  get #selecteur() {
    return /** @type {any} */ (this.#portee.querySelector('quantity-selector-component'));
  }

  get #champ() {
    return /** @type {HTMLInputElement | null} */ (this.#portee.querySelector('input[name="quantity"]'));
  }

  /** @param {Event} event */
  #surChoix = (event) => {
    const radio = event.target;
    if (!(radio instanceof HTMLInputElement) || radio.type !== 'radio') return;
    this.#regler(Number(radio.value) || 1);
  };

  #surQuantite = () => this.#refleter();

  /**
   * Nouveau format choisi : on reprend l'encart tel que le serveur vient de le rendre,
   * prix et économie compris.
   * @param {any} event
   */
  #surVariante = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const neuf = detail.html.querySelector('luxe-duo');

        // Format en rupture, ou fiche qui n'ouvre pas droit à l'offre : plus d'encart
        this.hidden = !neuf;
        if (!neuf) {
          this.replaceChildren();
          return;
        }

        const copie = /** @type {HTMLElement} */ (document.importNode(neuf, true));
        this.replaceChildren(...copie.childNodes);
        if (copie.dataset.percent) this.dataset.percent = copie.dataset.percent;

        // Le sélecteur de quantité du thème se re-rend lui aussi : on relit après lui
        this.#refleter();
        requestAnimationFrame(() => this.#refleter());
      })
      .catch((erreur) => {
        if (erreur?.name !== 'AbortError') console.warn('[luxe-duo] mise à jour impossible :', erreur);
      });
  };

  /**
   * Règle la quantité du thème, sans dépasser ce que le variant autorise encore.
   * @param {number} souhaitee
   */
  #regler(souhaitee) {
    const selecteur = this.#selecteur;
    const plafond = typeof selecteur?.getEffectiveMax === 'function' ? selecteur.getEffectiveMax() : null;
    const quantite = plafond == null ? souhaitee : Math.max(1, Math.min(souhaitee, plafond));

    // Le composant du thème sait se mettre à jour : boutons +/- et prix à l'unité suivent
    if (selecteur && typeof selecteur.setValue === 'function') {
      selecteur.setValue(String(quantite));
      selecteur.updateButtonStates?.();
      selecteur.onQuantityChange?.();
      return;
    }

    const champ = this.#champ ?? this.#creerChamp();
    if (!champ) return;
    champ.value = String(quantite);
    champ.dispatchEvent(new Event('change', { bubbles: true }));
    this.#refleter();
  }

  /** Fiche sans sélecteur de quantité : le formulaire en reçoit un, masqué. */
  #creerChamp() {
    const formulaire = this.#portee.querySelector('product-form-component form');
    if (!formulaire) return null;
    const champ = document.createElement('input');
    champ.type = 'hidden';
    champ.name = 'quantity';
    champ.value = '1';
    formulaire.appendChild(champ);
    return champ;
  }

  /** Aligne les cartes sur la quantité réellement en place (3 rideaux ou plus : le lot reste coché). */
  #refleter() {
    const quantite = Number(this.#champ?.value) || 1;
    for (const radio of this.querySelectorAll('input[type="radio"]')) {
      if (!(radio instanceof HTMLInputElement)) continue;
      radio.checked = Number(radio.value) === 2 ? quantite >= 2 : quantite < 2;
    }
  }
}

if (!customElements.get('luxe-duo')) {
  customElements.define('luxe-duo', LuxeDuo);
}
